import { isFulfilled } from "@reduxjs/toolkit";
import { useContext } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { NO_DRAG_SELECTOR } from "../../../../app/constants";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { CustomButton } from "../../../../common/components/functional/customButton/CustomButton";
import OverlaySpinner from "../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import isEmpty from "../../../../common/helpers/isEmpty";
import { AccessRightContext } from "../../../user/AccessRightProvider";
import { CustomerContract } from "../../customerAPI";
import { updateContractPricing } from "../../customerContractsSlice";
import { ActionButtonsContainer, ModalContainer } from "../../styles";
import {
  getPricingPerTesFormDefaultValues,
  handleTesObjectForApi,
} from "../helpers/pricingUtils";
import { StyledFormGroup } from "../styles";
import PricingFormModalHeader from "./form/common/PricingFormModalHeader";
import TesSection from "./form/common/TesSection";
import PricingSpecialSection from "./form/specials/PricingSpecialSection";
import WorkAndAbsencesSection from "./form/workAndAbscences/WorkAndAbsencesSection";
import WorkTitleSpecificPricingSection from "./form/workTitles/WorkTitleSpecificPricingSection";
import { UnsavedChangesProvider } from "../../../../contexts/UnsavedChangesContext";

export interface PricingPerTesData {
  pricingValidFromDate: string;
  existingPricingObjectFromApi?: any;
  pricingTesItem: object;
}

interface Props {
  openedFromPricingContent?: boolean;
  contractData: CustomerContract;
  pricingPerTesData: PricingPerTesData;
  eezyCompanyName: string;
  closeModal: () => void;
}

const PricingPerTesForm = ({
  openedFromPricingContent,
  contractData,
  pricingPerTesData,
  eezyCompanyName,
  closeModal,
}: Props) => {
  const { t } = useTranslation("customers");
  const { getValues: getMainFormValues, setValue: setMainFormValue } =
    useFormContext() || {};
  // Depending on from where the form is opened, form context may not be available
  const dispatch = useAppDispatch();
  const { tes } = useAppSelector((state) => state.localized);

  const { status } = useAppSelector((state) => state.customerContracts);
  const isLoading = status === "pending";

  const existingTesArray =
    (openedFromPricingContent && getMainFormValues("pricingTesList")) || [];

  const { pricingValidFromDate, existingPricingObjectFromApi, pricingTesItem } =
    pricingPerTesData;

  const defaultValues = getPricingPerTesFormDefaultValues(pricingTesItem);

  const { canEditContract } = useContext(AccessRightContext);

  const methods = useForm({ defaultValues, mode: "onChange" });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, isSubmitting, errors },
  } = methods;

  const onSubmit = (formData: any) => {
    if (openedFromPricingContent) {
      // Update only main form (new item or editing existing)
      const tesItemObject = {
        tesId: formData.tesId,
        categories: formData.categories,
        specials: formData.specials,
        worktitles: formData.worktitles,
      };
      // Check if new item or editing existing
      const existingTesItem = existingTesArray.find(
        (tesItem: any) => tesItem.tesId === tesItemObject.tesId,
      );
      const updatedTesArray = existingTesItem
        ? existingTesArray.map((tesItem: any) => {
            if (tesItem.tesId === tesItemObject.tesId) return tesItemObject;
            return tesItem;
          })
        : [...existingTesArray, tesItemObject];

      setMainFormValue("pricingTesList", updatedTesArray, {
        shouldDirty: true,
      });
      closeModal();
    } else {
      // Update whole contract pricing with one changed TES item

      const updatedPricing = {
        id: contractData.id,
        version: contractData.version,
        validFrom: pricingValidFromDate,
        pricing: {
          ...existingPricingObjectFromApi,
          tes: {
            ...existingPricingObjectFromApi?.tes,
            ...handleTesObjectForApi(formData),
          },
        },
      };

      dispatch(updateContractPricing(updatedPricing)).then((action) => {
        if (isFulfilled(action)) closeModal();
      });
    }
  };

  const onSave = () => {
    handleSubmit(onSubmit)();
  };

  const onCancel = () => {
    reset(defaultValues);
  };

  const handleClose = () => {
    onCancel();
    closeModal();
  };

  const watchedTesId = watch("tesId");
  const selectedTesName = watchedTesId ? tes[watchedTesId] : "";

  const editingExistingPricingPerTes = !isEmpty(pricingTesItem);
  const tesSelectionIsDisabled =
    !!!openedFromPricingContent || editingExistingPricingPerTes;

  // TODO rules for disabling content editing
  // const isPastDate =
  //   new Date(pricingValidFromDate).getTime() < new Date().getTime();
  // const contentDisabled = !openedFromPricingContent && isPastDate;
  const contentDisabled = false;

  const saveDisabled =
    !isDirty ||
    !watchedTesId ||
    !isEmpty(errors) ||
    contentDisabled ||
    isSubmitting ||
    isLoading;

  return (
    <ModalContainer>
      <PricingFormModalHeader
        pricingValidFromDate={pricingValidFromDate}
        selectedItemName={selectedTesName}
        workLocationName={eezyCompanyName}
        handleClose={handleClose}
      />
      <FormProvider {...methods}>
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <StyledFormGroup className={NO_DRAG_SELECTOR}>
            <OverlaySpinner isLoading={isLoading} />
            <TesSection
              disabled={tesSelectionIsDisabled || contentDisabled}
              existingTesArray={existingTesArray}
            />
            <WorkAndAbsencesSection
              tesId={watchedTesId}
              disabled={contentDisabled}
              canEdit={canEditContract(contractData.eezyCompany.id)}
            />
            <PricingSpecialSection disabled={contentDisabled} />
            <WorkTitleSpecificPricingSection
              disabled={contentDisabled}
              canEdit={canEditContract(contractData.eezyCompany.id)}
            />
          </StyledFormGroup>
        </UnsavedChangesProvider>
      </FormProvider>
      <ActionButtonsContainer marginTop="1rem">
        <CustomButton
          accessRightChecks={[canEditContract(contractData.eezyCompany.id)]}
          variant="contained"
          sx={{ borderRadius: "15px", height: "30px" }}
          onClick={onSave}
          disabled={saveDisabled}
        >
          {openedFromPricingContent ? t("common:complete") : t("common:save")}
        </CustomButton>
      </ActionButtonsContainer>
    </ModalContainer>
  );
};

export default PricingPerTesForm;
